import React, { createContext, useContext } from "react";

import { NotificationDto, NotificationsListObject } from "@megaron/notifications-contracts";
import { useClientManager } from "@megaron/react-clients";

interface NotificationsContextType {
  data: NotificationsListObject | undefined;
  key: string | string[];
  refetch: () => void;
  isFetching: boolean;
}

const NotificationsContext = createContext<NotificationsContextType>({
  data: undefined,
  key: "notifications",
  refetch: () => undefined,
  isFetching: false,
});

export const NotificationsProvider: React.FC<{
  children: React.ReactNode;
  isAuthLoaded: boolean;
}> = ({ children, isAuthLoaded }) => {
  const notificationsQuery = useClientManager("notifications").getNotifications().useQuery(
    {},
    {
      enabled: false,
      refetchInterval: false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );

  return (
    <NotificationsContext.Provider
      value={{
        data: notificationsQuery.data,
        key: "notifications",
        refetch: notificationsQuery.refetch,
        isFetching: notificationsQuery.isFetching,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const { data: notifications, key, refetch, isFetching } = useContext(NotificationsContext);

  const getUnreadNotifications = (): {
    notifications: NotificationDto[];
    count: number;
  } => {
    if (!notifications) {
      return {
        notifications: [],
        count: 0,
      };
    }
    const unreadNotifications = notifications.notifications.filter((notification) => !notification.readAt);
    return {
      notifications: unreadNotifications,
      count: unreadNotifications.length,
    };
  };

  return {
    allNotifications: notifications,
    queryKey: key,
    unreadNotifications: getUnreadNotifications,
    refetchNotifications: refetch,
    isFetchingNotifications: isFetching,
  };
};
